import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import { rupiah } from '@lib/currency';
import { RenderBankLogoName } from '@components/RenderBankPaymentMethod';

import TimeCountdown from './TimeCountdown';
import AccordionItem from './Accordion';
import HeaderResult from '@components/Result/HeaderResult';
import { useCheckoutStore } from '@stores/useCheckoutStore';
import { SettingsProps, DataDetailCheckout } from '@pages/types';

interface RedirectResultProps {
  data: DataDetailCheckout;
  settings: SettingsProps;
  merchantName?: string;
  onPaymentSelected?: () => void;
  onGoToOverview?: () => void;
  onRefreshData?: () => void;
}

const formatPhoneDisplay = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('62')) {
    const local = digits.slice(2);
    const part1 = local.slice(0, 3);
    const part2 = local.slice(3, 7);
    const part3 = local.slice(7);
    return `+62 ${[part1, part2, part3].filter(Boolean).join('-')}`;
  }
  return `+${digits}`;
};

const RedirectResult: React.FC<RedirectResultProps> = ({ data, settings }) => {
  const [accordionOpen, setAccordionOpen] = useState(true);
  const { submittedCustomerNo } = useCheckoutStore();
  const location = useLocation();
  const { payment_type } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  }) as { payment_type?: string };

  const paymentCategory = (data?.payment?.type || '').toLowerCase();
  const isCC = paymentCategory === 'cards' || paymentCategory === 'card';

  const methodName =
    data?.payment?.string || (isCC ? 'Credit Card' : 'E-Wallet');
  const redirectUrl =
    data?.payment?.host_to_host_payment_info?.web_redirect_url;

  const merchantName = data?.merchant?.name || '-';
  const totalAmount = rupiah(data.total) || '-';

  const instructions = isCC
    ? [
        `Anda akan diarahkan ke halaman pembayaran ${methodName}.`,
        'Klik tombol di bawah jika halaman tidak terbuka secara otomatis.',
        `Periksa detail pembayaran dengan teliti. Merchant: ${merchantName}, Total Pembayaran: ${totalAmount}.`,
        'Masukkan detail kartu Anda dan selesaikan verifikasi (misalnya OTP 3D Secure).',
        'Setelah pembayaran berhasil, Anda akan menerima notifikasi konfirmasi.',
        'Kembali ke halaman ini untuk mengecek status pembayaran Anda.',
      ]
    : [
        `Permintaan pembayaran telah dikirim ke aplikasi ${methodName} Anda.`,
        `Klik tombol "Buka ${methodName}" di bawah untuk membuka aplikasi jika belum terbuka otomatis.`,
        `Periksa detail pembayaran dengan teliti. Merchant: ${merchantName}, Total Pembayaran: ${totalAmount}.`,
        `Selesaikan otorisasi pembayaran di aplikasi ${methodName} Anda.`,
        'Setelah pembayaran berhasil, Anda akan menerima notifikasi konfirmasi.',
        'Kembali ke halaman ini untuk mengecek status pembayaran Anda.',
      ];

  const buttonLabel = isCC
    ? `Open ${methodName} Payment Page`
    : `Open ${methodName}`;
  const subtitle = isCC
    ? `Complete your ${methodName} payment securely`
    : `Complete your payment through your ${methodName} application`;

  const handleOpen = () => {
    if (redirectUrl) {
      window.open(redirectUrl, '_blank');
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <HeaderResult title={methodName} subtitle={subtitle} />

      <TimeCountdown expirationTime={data?.payment_link_expiration} />

      <div>
        <RenderBankLogoName
          title={payment_type || 'Not Found'}
          width="w-[120px]"
          height="h-[50px]"
        />
      </div>

      {submittedCustomerNo && (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 p-6 shadow-sm">
          <p className="text-sm text-gray-500">Payment sent to phone number</p>
          <p className="text-2xl font-bold tracking-wide text-gray-900">
            {formatPhoneDisplay(submittedCustomerNo)}
          </p>
          <p className="text-xs text-gray-400">
            Registered phone number for {methodName}
          </p>
        </div>
      )}

      {redirectUrl && (
        <button
          onClick={handleOpen}
          className="w-full rounded-lg px-6 py-4 font-semibold transition-opacity hover:opacity-90"
          style={{
            backgroundColor: settings.button_color
              ? settings.button_color
              : '#5D87FF',
            color: settings.button_text_color
              ? settings.button_text_color
              : '#FFFFFF',
          }}
        >
          {buttonLabel}
        </button>
      )}

      <div>
        <div className="flex flex-col gap-1">
          <AccordionItem
            title={`How to Pay with ${methodName}`}
            content={
              <div className="flex flex-col gap-4">
                {instructions.map((instruction, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-sm font-semibold">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">{instruction}</p>
                    </div>
                  </div>
                ))}
              </div>
            }
            isOpen={accordionOpen}
            onToggle={() => setAccordionOpen(!accordionOpen)}
          />
        </div>
      </div>
    </div>
  );
};

export default RedirectResult;
